/**
 * Documentation written by: ChatGPT - Henrik
 * This file defines a centralized object for managing route paths in the application.
 * Using this object helps avoid hardcoding routes throughout the codebase, improving maintainability and consistency.
 *
 * - `Home`: Path for the home page (`"/"`).
 * - `Login`: Path for the login page (`"/login"`).
 * - `Register`: Path for the registration page (`"/register"`).
 * - `Admin`: Path for the admin dashboard (`"/admin"`).
 * - `AdminCompanyDetails`: Dynamic path for accessing company details in the admin panel, identified by a company `id`.
 *   Example: `AdminCompanyDetails("123")` returns `"/admin/c/123"`.
 *
 * Example Usage:
 * - Import and use these constants wherever route paths are required.
 * - Dynamic routes like `AdminCompanyDetails` simplify path generation with parameters.
 */
const pages = {
	MissingGroup: "/",
	Home: (groupID: string = ":id") => `/${groupID}`,
	ClientPortalCalculator: (groupID: string = ":id") => `/${groupID}/calculator`,
	ClientPortalDocuments: (groupID: string = ":id") => `/${groupID}/documents`,
	Login: "/login",
	Register: "/register",
	Admin: "/admin",
	AdminMainAccountCreate: "/admin/main-account/create",
	AdminMainAccountUpdate: (
		mainAccId: string = ":mainAccountId",
		accountId?: string
	) => {
		if (accountId) {
			return `/admin/main-account/${mainAccId}/update#${accountId}`;
		}
		return `/admin/main-account/${mainAccId}/update`;
	},
	AdminMainAccountDetails: (mainAccId: string = ":mainAccountId") =>
		`/admin/main-account/${mainAccId}`,
	AdminAccount: (
		mainAccId: string = ":mainAccId",
		accountId: string = ":accountId"
	) => `/admin/main-account/${mainAccId}/account/${accountId}`,
	AdminCoverageMatrix: (
		mainAccId: string = ":mainAccId",
		accountId: string = ":accountId",
		groupID: string = ":groupId"
	) => pages.AdminAccount(mainAccId, accountId) + `/coverage-matrix/${groupID}`,
	GValuePage: "/admin/gvalue",
	AdminProductDefinitions: "/admin/product-definitions",
};

export default pages;
